// Archivierte Stellen (D16). Zu einer archivierten Stelle kann ein Talent das
// Assessment nicht mehr durchlaufen, so verspricht es der Archivieren-Dialog.
// Neu einladen, „Bearbeitungszeit verlängern" und „Vollständig zurücksetzen"
// sind deshalb gesperrt, bis die Stelle wiederhergestellt ist. Das gilt auch
// für Erinnern, denn es schickt die Einladung erneut und setzt die Frist neu
// (bei abgelaufener Frist mindestens 14 Tage), öffnet das Assessment also
// wieder. Bestehende Links laufen weiter, das Backend sperrt erst in Phase 2.
// Das ist ein Bugfix und gilt in jedem Modus, nicht nur in enforced.
//
// Reine Funktionen ohne Imports, damit Jest sie direkt testen kann. Woher die
// Stellen kommen, entscheidet hooks/use-archived-career-ids.js.

export const ARCHIVED_CAREER_ACTIONS = {
  REINVITE: 'REINVITE',
  SEND_REMINDER: 'SEND_REMINDER',
  RESET_EXPIRED: 'RESET_EXPIRED',
  FULL_RESET: 'FULL_RESET'
}

export const ARCHIVED_CAREER_HINTS = {
  [ARCHIVED_CAREER_ACTIONS.REINVITE]: 'pricing.archived_career.reinvite_hint',
  [ARCHIVED_CAREER_ACTIONS.SEND_REMINDER]:
    'pricing.archived_career.remind_hint',
  [ARCHIVED_CAREER_ACTIONS.RESET_EXPIRED]:
    'pricing.archived_career.reset_expired_hint',
  [ARCHIVED_CAREER_ACTIONS.FULL_RESET]:
    'pricing.archived_career.full_reset_hint'
}

export const ARCHIVED_CAREER_SKIPPED = 'pricing.archived_career.skipped'
export const ARCHIVED_CAREER_RESTORE = 'pricing.archived_career.restore_action'
export const RECOVER_CAREERS_PATH = '/recover/careers'

// Talentpool und Attract tragen keine echte Stelle
const NON_CAREER_IDS = ['TALENTPOOL', 'ATTRACT']

export const isArchivedCareer = (career) => career?.archived === true

export const getSpaceCareerId = (space) => {
  const id = space?.career_id
  if (typeof id !== 'string' || !id || NON_CAREER_IDS.includes(id)) return null

  return id
}

// Eindeutige Stellen-IDs der Talente, sortiert, damit der Aufrufer sie als
// stabilen Schlüssel nutzen kann
export const collectSpaceCareerIds = (spaces) => {
  const ids = new Set()

  for (const space of spaces || []) {
    const id = getSpaceCareerId(space)
    if (id) ids.add(id)
  }

  return [...ids].sort()
}

// Map (careers_map), Liste, einzelne Stelle oder schon eine Funktion. Ergebnis
// ist immer eine Funktion id => Stelle oder undefined.
export const toCareerLookup = (careers) => {
  if (typeof careers === 'function') return careers
  if (careers instanceof Map) return (id) => careers.get(id)

  if (Array.isArray(careers)) {
    const byId = new Map()
    for (const career of careers) {
      if (career?.id) byId.set(career.id, career)
    }

    return (id) => byId.get(id)
  }

  if (careers?.id) return (id) => (id === careers.id ? careers : undefined)

  return () => undefined
}

// Je Stelle gilt der neueste Stand aus allen Quellen (`updatedAt` ist der
// dataUpdatedAt der Query). Bei Gleichstand gewinnt die spätere Quelle. So
// schlägt auf der Detailseite die frisch geladene Stelle eine alte Kopie aus
// dem persistierten Cache der Talentliste, in beide Richtungen.
export const resolveCareerStates = (careerIds, sources) => {
  const lookups = (sources || []).map(({ careers, updatedAt }) => ({
    find: toCareerLookup(careers),
    updatedAt: Number(updatedAt) || 0
  }))

  const archived = []
  const unknown = []

  for (const id of careerIds || []) {
    let newest
    let newestAt = -Infinity

    for (const { find, updatedAt } of lookups) {
      const career = find(id)
      if (career && updatedAt >= newestAt) {
        newest = career
        newestAt = updatedAt
      }
    }

    if (!newest) unknown.push(id)
    else if (isArchivedCareer(newest)) archived.push(id)
  }

  return { archived, unknown }
}

const toIdSet = (ids) => (ids instanceof Set ? ids : new Set(ids || []))

// Unbekannte Stellen gelten als nicht archiviert, wie heute (fail-open)
export const isSpaceOfArchivedCareer = (space, archivedCareerIds) => {
  const id = getSpaceCareerId(space)

  return !!id && toIdSet(archivedCareerIds).has(id)
}

export const partitionSpacesByArchivedCareer = (spaces, archivedCareerIds) => {
  const archivedIds = toIdSet(archivedCareerIds)
  const allowed = []
  const skipped = []

  for (const space of spaces || []) {
    if (isSpaceOfArchivedCareer(space, archivedIds)) skipped.push(space)
    else allowed.push(space)
  }

  return { allowed, skipped }
}

// Verfügbarkeit einer Talent-Aktion. `available` ist die bisherige
// Entscheidung (Assessment-Status), sie bleibt für alle anderen Aktionen und
// ohne archivierte Stelle unverändert. `hintKey` ist nur gesetzt, wenn die
// archivierte Stelle der einzige Grund ist, dann zeigt der Aufrufer diesen
// Hinweis statt des allgemeinen. War die Aktion schon wegen des Status nicht
// verfügbar, hilft Wiederherstellen nicht, dann bleibt der allgemeine Hinweis.
export const resolveSpaceActionAvailability = ({
  action,
  available,
  spaces,
  archivedCareerIds
}) => {
  const hintKey = ARCHIVED_CAREER_HINTS[action]
  const blocked =
    !!available &&
    !!hintKey &&
    (spaces || []).some((space) =>
      isSpaceOfArchivedCareer(space, archivedCareerIds)
    )

  if (!blocked) return { available, hintKey: null }

  return { available: false, hintKey }
}
